import { useMemo } from "react";
import { DataTable, SelectInput, type ColumnDef, type SelectOption } from "@repo/ui";
import { useUpdateUserPermissions } from "~/pages/administration/permissions/hooks/use-update-permission";
import type { Brand, Customer, User } from "~/types/api";
import { PermissionFilters } from "./filters/permission-filters";
import { usePermissionData } from "./hooks/use-permission-data";

const userRoles = {
  ["User"]: "Standard",
} as const;

type UserRoleKey = keyof typeof userRoles;
export type UserRole = (typeof userRoles)[keyof typeof userRoles];

type Props = {
  isDataLoading: boolean;
  isDataError?: boolean;
  users: User[];
  brands: Brand[];
  customers: Customer[];
};

export const Permissions = ({ isDataLoading, isDataError, users, brands, customers }: Props) => {
  const { getCustomerIdForBrand } = usePermissionData();

  const { addCustomer, removeCustomer, addBrand, removeBrand } = useUpdateUserPermissions();

  const roleOptions: SelectOption<string>[] = useMemo(
    () =>
      users
        .flatMap((user) => user.roles)
        .filter((role, index, arr) => arr.indexOf(role) === index)
        .map((role) => ({
          value: role,
          label: role,
        })),
    [users]
  );

  const customerOptions: SelectOption<number>[] = useMemo(
    () => customers.map((c) => ({ value: c.id, label: c.name })),
    [customers]
  );

  const brandOptions: SelectOption<number>[] = useMemo(
    () => brands.map((b) => ({ value: b.id, label: b.name })),
    [brands]
  );

  const handleCustomerChange = (user: User, newCustomerIds: number[]) => {
    const prevCustomerIds = user.customers.map((c) => c.customerId);

    newCustomerIds
      .filter((id) => !prevCustomerIds.includes(id))
      .forEach((id) => {
        addCustomer({ params: { path: { userId: user.id, customerId: id } } });
      });

    prevCustomerIds
      .filter((id) => !newCustomerIds.includes(id))
      .forEach((id) => {
        removeCustomer({ params: { path: { userId: user.id, customerId: id } } });
      });
  };

  const handleBrandChange = (user: User, newBrandIds: number[]) => {
    const prevBrandIds = user.brands.map((b) => b.brandId);

    newBrandIds
      .filter((id) => !prevBrandIds.includes(id))
      .forEach((brandId) => {
        const customerId = getCustomerIdForBrand(brandId);
        if (customerId) {
          addBrand({ params: { path: { userId: user.id, customerId, brandId } } });
        }
      });

    prevBrandIds
      .filter((id) => !newBrandIds.includes(id))
      .forEach((brandId) => {
        removeBrand(user.id, brandId);
      });
  };

  const columns: ColumnDef<User>[] = [
    {
      key: "name",
      header: "Name",
      width: "flex-1",
    },
    {
      key: "roles",
      header: "Roles",
      width: "flex-1",
      filterable: true,
      render: (renderValue, user) =>
        user.roles.map((role) => userRoles[role as UserRoleKey] ?? role).join(", "),
    },
    {
      key: "customers",
      header: "Customers",
      width: "flex-1",
      filterable: true,
      render: (_, user) => (
        <SelectInput
          options={customerOptions}
          value={user.customers.map((c) => c.customerId)}
          onChange={(customerIds) => handleCustomerChange(user, customerIds as number[])}
          placeholder="Select customers"
          multiple={true}
          showClearIcon={false}
        />
      ),
    },
    {
      key: "brands",
      header: "Brands",
      width: "flex-1",
      filterable: true,
      render: (_, user) => {
        return (
          <SelectInput
            options={brandOptions}
            value={user.brands.map((b) => b.brandId)}
            onChange={(brandIds) => handleBrandChange(user, brandIds as number[])}
            placeholder="Select brands"
            multiple={true}
            showClearIcon={false}
          />
        );
      },
    },
  ];

  return (
    <DataTable<User>
      isLoading={isDataLoading}
      isError={isDataError}
      data={users}
      columns={columns}
      className="overflow-x-auto"
      renderColumnFilters={
        users?.length > 0
          ? (_, setColumnFilter, filters) => (
              <PermissionFilters<User>
                setColumnFilter={setColumnFilter}
                filters={filters}
                roleOptions={roleOptions}
                customerOptions={customerOptions}
                brandOptions={brandOptions}
              />
            )
          : undefined
      }
    />
  );
};
